"use client";

import type { TeamAgent } from "@bash/shared";
import { kinIndexFor } from "@bash/shared";
import { cn } from "@/lib/utils";
import { BashFace, type FaceMood } from "@/components/bash-home/BashFace";
import { KinFace, type KinSize } from "@/components/bash-home/KinFace";

/**
 * The copper box each kin size asks for. The mock draws the lead and every
 * agent in one 30px row (.face--desk on both), so `desk` maps to the copper
 * mark's own `desk`; `chip` maps to `nav`, the same 24. The two smallest kin
 * boxes (16, 18) have no copper below 24, so they take the nearest rather
 * than a size the mark cannot draw.
 */
const COPPER_BOX: Record<KinSize, "nav" | "desk"> = {
  step: "nav",
  line: "nav",
  chip: "nav",
  desk: "desk",
};

/**
 * One face for the team (spec 2026-09-19 §3.3, §20 A13): the lead is Bash's
 * own copper mark; an agent is Bash's kin in the tint kinIndexFor(id) picks,
 * the same tint on the strip, in the pill, and (train 2) beside a channel
 * reply. No new face component and no new selector: KinFace's doubled
 * `.bh-face.bh-face--kin` rule is what paints the tint.
 *
 * The face is decoration in both arms, because the tile's own words name the
 * agent, so the lead's box is hidden from the accessibility tree the way
 * KinFace hides its own. Left visible, its `title` falls back into the accessible name
 * and a tile reads "Bash Bash lead".
 */
export function AgentFace({
  agent,
  size = "chip",
  mood = "content",
  className,
}: {
  agent: Pick<TeamAgent, "id" | "kind" | "name">;
  size?: KinSize;
  mood?: FaceMood;
  className?: string;
}) {
  if (agent.kind === "lead") {
    return (
      <span
        className={cn("inline-grid place-items-center", className)}
        title={agent.name}
        aria-hidden="true"
      >
        <BashFace size={COPPER_BOX[size]} mood={mood} />
      </span>
    );
  }
  return (
    <KinFace
      kin={kinIndexFor(agent.id)}
      size={size}
      mood={mood}
      title={agent.name}
      className={className}
    />
  );
}
